import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Skeleton,
  Switch,
  Typography,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LanguageIcon from '@mui/icons-material/Language';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { colors, typography } from '../styles/theme';
import { authInputAutofillSx } from '../Access/styles/authPanel.styles';
import { toast } from './Toast';
import {
  getUserLotListShare,
  shareUserLotList,
  unshareUserLotList,
} from '../services/userLotsShareService';
import { useShareDialogBrandsOverlap } from './BrandsModal/BrandsModalContext';
import { getMyBrands } from '../services/brandsService';
import ShareTemplateSection, {
  SHARE_TEMPLATE_DEFAULT,
  normalizeShareBrandId,
  normalizeShareTemplate,
  readShareApiError,
  resolveShareAppearance,
} from './ShareTemplateSection';
import {
  getShareDialogFooterScrollHintSx,
  useShareDialogScrollHint,
} from './shareDialogScrollHint';
import {
  getShareDangerButtonSx,
  getSharePrimaryButtonSx,
} from './shareDialogButtonSx';
import { FORM_SWITCH_SX } from '../Map/components/DockPanel/Tabs/formBlockUi';
import { buildEmailAllowlist } from '../Map/utils/shareAllowlist';
import {
  SHARE_PERSON_ROLE_EDITOR,
  SHARE_PERSON_ROLE_VIEWER,
  loadShareEditorEmails,
  mergeSharePeopleEmails,
  syncShareEditors,
} from '../Map/utils/sharePeopleRoles';
import SharePersonRoleSelect from './SharePersonRoleSelect';
import { resolveSpaceGuestWriteError } from '../Map/utils/spaceMirrorLots';

const BG_PAPER = '#1A1A1A';
const BG_ELEVATED = '#242424';
const BORDER = '#383838';
const TEXT_PRIMARY = '#F9FAFB';
const TEXT_SECONDARY = '#ABABAB';
const TEXT_MUTED = 'rgba(255,255,255,0.45)';
const DANGER = '#EE5757';
const WARNING_ACCENT = '#FFB74D';
const WARNING_ICON_BG = '#F57C00';

const SHARE_SECTION_SHELL_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  border: `1px solid ${BORDER}`,
  borderRadius: '8px',
  p: 1.5,
};

const SHARE_EMAIL_INPUT_SX = {
  flex: 1,
  minWidth: 140,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: TEXT_PRIMARY,
  fontFamily: typography.fontFamily.primary,
  fontSize: '13px',
  padding: '2px 0',
  ...authInputAutofillSx,
};

const SHARE_DIALOG_MAX_HEIGHT = '80vh';

const SHARE_INVITE_LIST_MAX_HEIGHT = 160;

const SHARE_DIALOG_SCROLLBAR_SX = {
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(255,255,255,0.10) transparent',
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255,255,255,0.10)',
    borderRadius: 8,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
};

const SHARE_DIALOG_PAPER_SX = {
  bgcolor: BG_PAPER,
  borderRadius: '12px',
  overflow: 'hidden',
  maxHeight: SHARE_DIALOG_MAX_HEIGHT,
  display: 'flex',
  flexDirection: 'column',
};

const SHARE_DIALOG_CONTENT_SX = {
  px: 2.5,
  pt: 2,
  pb: 1,
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  ...SHARE_DIALOG_SCROLLBAR_SX,
};

const SHARE_DIALOG_LIST_META_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  width: '100%',
  flexShrink: 0,
  px: 2.5,
  pt: 2,
  pb: 0.5,
  bgcolor: BG_PAPER,
};

const INVITE_LIST_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  maxHeight: SHARE_INVITE_LIST_MAX_HEIGHT,
  overflowY: 'auto',
  pr: 0.5,
  ...SHARE_DIALOG_SCROLLBAR_SX,
  '&::-webkit-scrollbar': { width: 5 },
};

function buildSharedListUrl(token) {
  return `${window.location.origin}/#/shared/${encodeURIComponent(token)}`;
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function normalizeEmail(raw) {
  return String(raw || '').trim().toLowerCase();
}

/** Iniciales a partir del local-part del email (ej. maria.garcia → MG). */
function getEmailInitials(email) {
  const local = String(email || '').split('@')[0] || '';
  const parts = local.split(/[._\-+]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
  const clean = local.replace(/[^a-zA-Z0-9]/g, '');
  if (clean.length >= 2) return clean.slice(0, 2).toUpperCase();
  if (clean.length === 1) return clean.toUpperCase();
  return '?';
}

function sameEmailSet(a = [], b = []) {
  if (a.length !== b.length) return false;
  const left = [...a].map(String).sort();
  const right = [...b].map(String).sort();
  return left.every((value, idx) => value === right[idx]);
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      return true;
    } catch {
      return false;
    }
  }
}

const SCOPE_OPTIONS = [
  { id: 'all', label: 'Todos' },
  { id: 'public', label: 'Solo públicos' },
  { id: 'private', label: 'Solo privados' },
];

const skTextSx = { bgcolor: 'rgba(255,255,255,0.10)', borderRadius: 1 };
const skMutedTextSx = { bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 1 };

const skFooterButtonSx = {
  bgcolor: 'rgba(255,255,255,0.10)',
  borderRadius: '8px',
  flexShrink: 0,
};

function ShareDialogContentSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Skeleton variant="text" width="40%" height={16} sx={skTextSx} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
          <Skeleton variant="rounded" height={72} sx={skTextSx} />
          <Skeleton variant="rounded" height={72} sx={skTextSx} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Skeleton variant="text" width="36%" height={14} sx={skMutedTextSx} />
        <Skeleton variant="rounded" width="100%" height={44} sx={skTextSx} />
      </Box>
    </Box>
  );
}

function AccessModeCard({ selected, icon, title, description, onClick, disabled }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 0.75,
        p: 1.5,
        m: 0,
        textAlign: 'left',
        cursor: disabled ? 'default' : 'pointer',
        borderRadius: '8px',
        border: selected ? `1.5px solid ${colors.primary}` : `1px solid ${BORDER}`,
        bgcolor: selected ? 'rgba(72,146,145,0.12)' : BG_ELEVATED,
        color: 'inherit',
        font: 'inherit',
        transition: 'border-color 0.15s, background-color 0.15s',
        opacity: disabled ? 0.6 : 1,
        '&:hover': disabled ? undefined : {
          borderColor: selected ? colors.primary : 'rgba(255,255,255,0.28)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ color: selected ? colors.primaryLight : TEXT_SECONDARY, display: 'flex' }}>
          {icon}
        </Box>
        <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <Typography sx={{ color: TEXT_MUTED, fontSize: '11px', lineHeight: 1.35 }}>
        {description}
      </Typography>
    </Box>
  );
}

/**
 * Compartir una lista. `ownerUserId` (espacio ajeno, F3): el invitado comparte
 * la lista del owner. Lee la config con `?owner_user_id=`, reenvía la config
 * completa con `owner_user_id` en el body, muestra la apariencia del owner fija
 * (sin las marcas del invitado) y no ofrece invitar editores (la API → 403).
 * Sin `ownerUserId` → igual que siempre. Ver docs/maps/espacios-invite.md.
 */
function ShareUserLotListDialog({
  open,
  listId,
  listName = '',
  lotCount = null,
  unitCount = null,
  lotContentCount = null,
  ownerUserId = null,
  onClose,
}) {
  const spaceOwner = ownerUserId ? String(ownerUserId).trim() : '';
  const isSpaceGuest = Boolean(spaceOwner);
  const brandsOverlap = useShareDialogBrandsOverlap();
  const [scope, setScope] = useState('all');
  const [accessMode, setAccessMode] = useState('public');

  const [emailInput, setEmailInput] = useState('');
  const [emailInputError, setEmailInputError] = useState('');
  const [emails, setEmails] = useState([]);
  /** Allowlist original al abrir (para detectar cambios y label del footer). */
  const [originalEmails, setOriginalEmails] = useState([]);
  const [editorEmails, setEditorEmails] = useState([]);
  const [originalEditorEmails, setOriginalEditorEmails] = useState([]);
  /** Notificar invitados al dar/actualizar acceso (off por defecto). */
  const [notifyUsers, setNotifyUsers] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifyMessageError, setNotifyMessageError] = useState('');
  const [loading, setLoading] = useState(false);
  const [shareConfigLoaded, setShareConfigLoaded] = useState(false);
  const [shareToken, setShareToken] = useState('');
  /** 'none' | 'active' | 'inactive'
   *  none     = nunca se generó un enlace
   *  active   = is_shared true + token válido
   *  inactive = se desactivó (puede quedar token histórico)
   */
  const [linkStatus, setLinkStatus] = useState('none');
  const [revokedShareUrl, setRevokedShareUrl] = useState('');
  /** Modo del share guardado en servidor: solo ese tab muestra el enlace. */
  const [savedAccessMode, setSavedAccessMode] = useState(null);
  const [shareTemplate, setShareTemplate] = useState(SHARE_TEMPLATE_DEFAULT);
  const [savedShareTemplate, setSavedShareTemplate] = useState(SHARE_TEMPLATE_DEFAULT);
  /** `null` = seguí la marca principal de la empresa. */
  const [brandId, setBrandId] = useState(null);
  const [savedBrandId, setSavedBrandId] = useState(null);
  const [showCadastralBase, setShowCadastralBase] = useState(true);
  const [savedShowCadastralBase, setSavedShowCadastralBase] = useState(true);
  /** Espacio ajeno: se reenvía tal cual para no pisar la config del owner. */
  const [savedInquiryEmailSource, setSavedInquiryEmailSource] = useState(null);
  /** Espacio ajeno: sin la config del owner no se guarda (pisaría su marca). */
  const [guestConfigMissing, setGuestConfigMissing] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [inactiveWarningVisible, setInactiveWarningVisible] = useState(true);

  const inputRef = useRef(null);

  const shareUrl = useMemo(() => (shareToken ? buildSharedListUrl(shareToken) : ''), [shareToken]);

  const isInitializing = loading || (open && !shareConfigLoaded);
  const isPrivate = accessMode === 'restricted';
  /** El enlace/estado solo aplica al tab que coincide con el share guardado. */
  const linkMatchesTab = savedAccessMode === accessMode;
  const effectiveLinkStatus = linkMatchesTab ? linkStatus : 'none';
  const hasActiveLink = effectiveLinkStatus === 'active' && Boolean(shareToken);
  const displayLinkUrl = hasActiveLink
    ? shareUrl
    : (effectiveLinkStatus === 'inactive' ? revokedShareUrl : '');
  const scrollHintKey = [
    open,
    isPrivate,
    isInitializing,
    emails.length,
    shareTemplate,
    effectiveLinkStatus,
  ].join('|');
  const { contentRef, canScrollMore } = useShareDialogScrollHint(scrollHintKey);
  const allowlistDirty = !sameEmailSet(emails, originalEmails);
  const editorsDirty = !sameEmailSet(editorEmails, originalEditorEmails);
  const templateDirty = shareTemplate !== savedShareTemplate
    || brandId !== savedBrandId;
  const cadastralDirty = showCadastralBase !== savedShowCadastralBase;
  const privatePrimaryLabel = (hasActiveLink || (linkMatchesTab && originalEmails.length > 0))
    ? 'Actualizar accesos'
    : 'Dar acceso';
  const privatePrimaryDisabled = privatePrimaryLabel === 'Dar acceso'
    ? emails.length === 0
    : !allowlistDirty && !editorsDirty && !templateDirty && !cadastralDirty;
  /** Público: Personas = solo editores; el enlace ya existe o hay que generar. */
  const publicDirty = editorsDirty || templateDirty || cadastralDirty;
  const hasNewInvitees = emails.some((email) => !originalEmails.includes(email));
  const guestBlocked = isSpaceGuest && guestConfigMissing;

  const resetState = () => {
    setScope('all');
    setAccessMode('public');
    setEmailInput('');
    setEmailInputError('');
    setEmails([]);
    setOriginalEmails([]);
    setEditorEmails([]);
    setOriginalEditorEmails([]);
    setNotifyUsers(false);
    setNotifyMessage('');
    setNotifyMessageError('');
    setLoading(true);
    setShareConfigLoaded(false);
    setShareToken('');
    setLinkStatus('none');
    setRevokedShareUrl('');
    setSavedAccessMode(null);
    setShareTemplate(SHARE_TEMPLATE_DEFAULT);
    setSavedShareTemplate(SHARE_TEMPLATE_DEFAULT);
    setBrandId(null);
    setSavedBrandId(null);
    setShowCadastralBase(true);
    setSavedShowCadastralBase(true);
    setSavedInquiryEmailSource(null);
    setGuestConfigMissing(false);
    setSubmitting(false);
    setError('');
    setInactiveWarningVisible(true);
  };

  useEffect(() => {
    if (!open) return;
    resetState();
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    if (!open || !listId) return undefined;

    setLoading(true);
    setError('');
    setShareToken('');
    setLinkStatus('none');
    setRevokedShareUrl('');
    setSavedAccessMode(null);

    (async () => {
      try {
        // Espacio ajeno: config del owner; los editores por lista son del dueño
        // (getlistmembers es solo owner), así que no se piden.
        const [res, memberEmails] = await Promise.all([
          isSpaceGuest
            ? getUserLotListShare(listId, { ownerUserId: spaceOwner })
            : getUserLotListShare(listId),
          isSpaceGuest ? Promise.resolve([]) : loadShareEditorEmails('list', listId),
        ]);
        if (cancelled) return;

        if (res?._httpStatus === 404) {
          // Propio espacio: sin fila de share → "nunca compartida".
          // Espacio ajeno: 404 = lista que no es del owner → no abrir writable.
          if (isSpaceGuest) {
            setError(resolveSpaceGuestWriteError(404, spaceOwner)
              || 'No encontramos este elemento en el espacio.');
            setGuestConfigMissing(true);
            setShareConfigLoaded(true);
            setLoading(false);
            return;
          }
          setLinkStatus('none');
          setSavedAccessMode(null);
          setEmails([]);
          setOriginalEmails([]);
          setEditorEmails([...memberEmails]);
          setOriginalEditorEmails([...memberEmails]);
          setShareConfigLoaded(true);
          setLoading(false);
          return;
        }

        if (!res?._httpOk) {
          setError(
            (isSpaceGuest && resolveSpaceGuestWriteError(res?._httpStatus, spaceOwner))
            || 'No pudimos cargar la configuración. Intentalo de nuevo.',
          );
          if (isSpaceGuest) setGuestConfigMissing(true);
          setEditorEmails([...memberEmails]);
          setOriginalEditorEmails([...memberEmails]);
          setShareConfigLoaded(true);
          setLoading(false);
          return;
        }

        const data = res?.data || res;
        const token = data?.share_token || data?.shareToken || '';
        const isShared = data?.is_shared === true || data?.isShared === true;
        const nextScope = data?.share_scope || data?.scope || 'all';
        const mode = data?.access_mode || data?.accessMode || 'public';
        const normalizedMode = mode === 'restricted' ? 'restricted' : 'public';
        const allowlist = Array.isArray(data?.allowlist) ? data.allowlist : [];

        // El backend puede devolver share_token aunque is_shared sea false (enlace desactivado).
        if (isShared && token) {
          setShareToken(token);
          setLinkStatus('active');
          setRevokedShareUrl('');
          setSavedAccessMode(normalizedMode);
        } else if (token) {
          setShareToken('');
          setLinkStatus('inactive');
          setRevokedShareUrl(buildSharedListUrl(token));
          setSavedAccessMode(normalizedMode);
        } else {
          setShareToken('');
          setLinkStatus('none');
          setRevokedShareUrl('');
          setSavedAccessMode(null);
        }

        setScope(SCOPE_OPTIONS.some((x) => x.id === nextScope) ? nextScope : 'all');
        setAccessMode(normalizedMode);
        const nextTemplate = normalizeShareTemplate(data?.share_template || data?.shareTemplate);
        setShareTemplate(nextTemplate);
        setSavedShareTemplate(nextTemplate);
        const nextBrandId = normalizeShareBrandId(data?.brand_id);
        setBrandId(nextBrandId);
        setSavedBrandId(nextBrandId);
        const nextCadastral = data?.show_cadastral_base !== false;
        setShowCadastralBase(nextCadastral);
        setSavedShowCadastralBase(nextCadastral);
        setSavedInquiryEmailSource(data?.inquiry_email_source ?? null);

        if (normalizedMode === 'restricted') {
          const preloadedEmails = allowlist
            .filter((x) => x?.type === 'email' && x?.value)
            .map((x) => String(x.value).toLowerCase());
          const uniqueEmails = mergeSharePeopleEmails(preloadedEmails, memberEmails);
          setEmails(uniqueEmails);
          setOriginalEmails(uniqueEmails);
        } else {
          // Público: no hay observadores; Personas lista solo editores.
          setEmails([...memberEmails]);
          setOriginalEmails([...memberEmails]);
        }
        setEditorEmails([...memberEmails]);
        setOriginalEditorEmails([...memberEmails]);

        setShareConfigLoaded(true);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError('No pudimos cargar la configuración. Intentalo de nuevo.');
        if (isSpaceGuest) setGuestConfigMissing(true);
        setShareConfigLoaded(true);
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, listId, spaceOwner, isSpaceGuest]);

  const tryCommitEmail = () => {
    const value = normalizeEmail(emailInput);
    if (!value) return;

    if (!isValidEmail(value)) {
      setEmailInputError('El email no tiene un formato válido.');
      return;
    }

    if (emails.includes(value)) {
      setEmailInputError('Ese email ya fue agregado.');
      return;
    }

    setEmails((prev) => [...prev, value]);
    // En público no hay observador: quien se agrega es editor.
    if (accessMode === 'public') {
      setEditorEmails((prev) => (prev.includes(value) ? prev : [...prev, value]));
    }
    setEmailInput('');
    setEmailInputError('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      tryCommitEmail();
    }
    if (e.key === 'Backspace' && !emailInput && emails.length > 0) {
      const removed = emails[emails.length - 1];
      setEmails((prev) => prev.slice(0, -1));
      setEditorEmails((prev) => prev.filter((item) => item !== removed));
      setEmailInputError('');
    }
  };

  const handleEmailChange = (e) => {
    const raw = e.target.value;
    if (raw.includes(',') || raw.includes(';')) {
      const parts = raw.split(/[,;]+/).map(normalizeEmail).filter(Boolean);
      const valid = [];
      const invalid = [];
      parts.forEach((p) => {
        if (!isValidEmail(p)) { invalid.push(p); return; }
        if (!emails.includes(p)) valid.push(p);
      });
      if (valid.length) {
        setEmails((prev) => [...prev, ...valid]);
        if (accessMode === 'public') {
          setEditorEmails((prev) => {
            const next = [...prev];
            valid.forEach((email) => {
              if (!next.includes(email)) next.push(email);
            });
            return next;
          });
        }
      }
      if (invalid.length) {
        setEmailInput(invalid.join(', '));
        setEmailInputError(`Algunos emails no son válidos: ${invalid.join(', ')}`);
      } else {
        setEmailInput('');
        setEmailInputError('');
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      return;
    }
    setEmailInput(raw);
    if (emailInputError) setEmailInputError('');
  };

  const handleEmailBlur = () => {
    if (emailInput.trim()) tryCommitEmail();
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const doSubmit = async () => {
    if (!listId) return;

    if (emailInput.trim()) {
      const value = normalizeEmail(emailInput);
      if (!isValidEmail(value)) {
        setEmailInputError('El email no tiene un formato válido.');
        return;
      }
      if (!emails.includes(value)) {
        setEmails((prev) => [...prev, value]);
        if (accessMode === 'public') {
          setEditorEmails((prev) => (prev.includes(value) ? prev : [...prev, value]));
        }
      }
      setEmailInput('');
      setEmailInputError('');
      await new Promise((r) => setTimeout(r, 0));
    }

    setError('');
    setSubmitting(true);
    try {
      const finalEmails = emailInput.trim()
        ? [...emails, normalizeEmail(emailInput)].filter((v, i, a) => a.indexOf(v) === i)
        : emails;

      const updatingPrivateAccess = accessMode === 'restricted'
        && (hasActiveLink || originalEmails.length > 0);
      const updatingPublicEditors = accessMode === 'public'
        && hasActiveLink
        && (originalEditorEmails.length > 0 || finalEmails.length > 0);

      if (accessMode === 'restricted' && finalEmails.length === 0 && !updatingPrivateAccess) {
        setError('Agregá al menos un email para poder continuar.');
        setSubmitting(false);
        return;
      }

      // Solo se notifica a emails nuevos (no estaban en Personas al abrir).
      const originalSet = new Set(originalEmails.map((e) => normalizeEmail(e)));
      const newInviteEmails = finalEmails.filter((e) => !originalSet.has(normalizeEmail(e)));

      // Espacio ajeno: invitar editores es del dueño (`as_editor` → 403).
      const nextEditors = isSpaceGuest
        ? []
        : (accessMode === 'public'
          ? finalEmails
          : editorEmails.filter((email) => finalEmails.includes(email)));

      // Restricted: allowlist de ver + as_editor en editores.
      // Público: allowlist solo si hay invite (todos con as_editor; observador → 400).
      const inviteMessage = notifyMessage.trim().slice(0, 2000);
      const wantsNotify = notifyUsers;
      if (wantsNotify && !inviteMessage) {
        setNotifyMessageError('Escribí un mensaje para notificar a los usuarios.');
        setSubmitting(false);
        return;
      }
      if (wantsNotify && newInviteEmails.length === 0) {
        setNotifyMessageError('Solo se notifica a personas nuevas. Agregá al menos un email nuevo.');
        setSubmitting(false);
        return;
      }
      const shouldNotify = wantsNotify && newInviteEmails.length > 0;

      let allowlist;
      if (accessMode === 'restricted') {
        allowlist = buildEmailAllowlist(finalEmails, { asEditorEmails: nextEditors });
      } else if (shouldNotify) {
        allowlist = buildEmailAllowlist(newInviteEmails, { asEditorEmails: newInviteEmails });
      }

      // Espacio ajeno: la apariencia es la del owner, tal como vino del GET
      // (sin mirar las marcas del invitado).
      let appearance = { shareTemplate, brandId };
      if (!isSpaceGuest) {
        const brandsRes = await getMyBrands();
        appearance = resolveShareAppearance(
          shareTemplate,
          brandId,
          brandsRes.ok ? brandsRes.brands : [],
        );
      }

      // Lista completa y viva: no enviar lot_ids / user_lot_ids (el backend responde 400).
      const res = await shareUserLotList({
        list_id: listId,
        scope,
        access_mode: accessMode,
        allowlist,
        share_template: appearance.shareTemplate,
        brand_id: appearance.brandId,
        show_cadastral_base: showCadastralBase,
        ...(isSpaceGuest
          ? { inquiry_email_source: savedInquiryEmailSource, ownerUserId: spaceOwner }
          : {}),
        ...(shouldNotify
          ? {
              send_invite: true,
              invite_message: inviteMessage,
              invite_emails: newInviteEmails,
            }
          : {}),
      });

      const token = res?.data?.share_token || res?.data?.token;
      if (!token) {
        setError(
          (isSpaceGuest && resolveSpaceGuestWriteError(res?._httpStatus, spaceOwner))
          || readShareApiError(
            res,
            'No se pudo guardar la configuración. Intentalo de nuevo.',
          ),
        );
        setSubmitting(false);
        return;
      }

      setShareToken(token);
      setLinkStatus('active');
      setRevokedShareUrl('');
      setSavedAccessMode(accessMode === 'restricted' ? 'restricted' : 'public');
      setSavedShareTemplate(
        normalizeShareTemplate(res?.data?.share_template || shareTemplate),
      );
      // Con `default` el backend limpia la marca, así que el estado local se
      // alinea con lo que quedó guardado en vez de conservar la última elección.
      const nextSavedBrandId = Object.prototype.hasOwnProperty.call(res?.data || {}, 'brand_id')
        ? normalizeShareBrandId(res.data.brand_id)
        : brandId;
      setSavedBrandId(nextSavedBrandId);
      setBrandId(nextSavedBrandId);
      const savedCadastral = res?.data?.show_cadastral_base;
      setSavedShowCadastralBase(
        typeof savedCadastral === 'boolean' ? savedCadastral : showCadastralBase,
      );
      setShowCadastralBase(
        typeof savedCadastral === 'boolean' ? savedCadastral : showCadastralBase,
      );

      // Sacar editor = DELETE /deletelistmember (no unshare). as_editor solo suma.
      // Espacio ajeno: los editores son del dueño, no se tocan.
      const synced = isSpaceGuest
        ? { ok: true, failed: [] }
        : await syncShareEditors(
          'list',
          listId,
          nextEditors,
          originalEditorEmails,
        );
      setEmails([...finalEmails]);
      setOriginalEmails([...finalEmails]);
      setEditorEmails(nextEditors);
      setOriginalEditorEmails(synced.ok ? nextEditors : originalEditorEmails);
      if (!synced.ok) {
        toast.warning(
          accessMode === 'restricted'
            ? 'Se guardó quién puede ver, pero no se pudieron actualizar todos los editores.'
            : 'Se guardó el enlace, pero no se pudieron actualizar todos los editores.',
        );
      }

      if (accessMode === 'restricted') {
        if (!updatingPrivateAccess) {
          void copyToClipboard(buildSharedListUrl(token));
        }

        if (updatingPrivateAccess && shouldNotify) {
          toast.success('Se notificó a las nuevas personas con acceso.', {
            title: 'Accesos actualizados.',
          });
        } else if (updatingPrivateAccess) {
          toast.success('Ya podés compartir el enlace con las nuevas personas.', {
            title: 'Accesos actualizados.',
          });
        } else if (shouldNotify) {
          toast.success('Se notificó a las personas y el enlace privado quedó activo.', {
            title: 'Accesos asignados.',
          });
        } else {
          toast.success('Se generó un enlace privado, y está disponible para compartir.', {
            title: 'Accesos asignados.',
          });
        }
      } else {
        const url = buildSharedListUrl(token);
        if (!updatingPublicEditors || !hasActiveLink) {
          const copied = await copyToClipboard(url);
          if (shouldNotify) {
            toast.success(
              copied
                ? 'Se notificó a los editores y el enlace quedó en el portapapeles.'
                : 'Se notificó a los editores y el enlace quedó activo.',
              { title: hasActiveLink ? 'Enlace actualizado.' : 'Enlace generado.' },
            );
          } else {
            toast.success(
              copied
                ? 'Enlace copiado en el portapapeles.'
                : 'Enlace generado correctamente.',
            );
          }
        } else if (shouldNotify) {
          toast.success('Se notificó a los nuevos editores.', {
            title: 'Editores actualizados.',
          });
        } else if (editorsDirty) {
          toast.success('Ya podés compartir el enlace con los editores.', {
            title: 'Editores actualizados.',
          });
        } else {
          toast.success('Enlace actualizado.');
        }
      }

      const invite = res?.data?.invite;
      if (Array.isArray(invite?.failed) && invite.failed.length > 0) {
        toast.warning(
          `No se pudo notificar a: ${invite.failed.join(', ')}`
        );
      }

      if (shouldNotify) {
        setNotifyUsers(false);
        setNotifyMessage('');
        setNotifyMessageError('');
      }
    } catch (e) {
      setError('Ocurrió un error al guardar. Intentalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (submitting || isInitializing || guestBlocked) return;
    if (accessMode === 'restricted') {
      if (privatePrimaryDisabled) return;
      // Actualizar puede persistir lista vacía si se quitaron todos los accesos.
      if (privatePrimaryLabel === 'Dar acceso' && emails.length === 0 && !emailInput.trim()) {
        setError('Agregá al menos un email para poder continuar.');
        setEmailInputError('Agregá al menos un email.');
        setTimeout(() => inputRef.current?.focus(), 0);
        return;
      }
    } else if (hasActiveLink && !publicDirty && !notifyUsers) {
      return;
    }
    if (notifyUsers && !notifyMessage.trim()) {
      setNotifyMessageError('Escribí un mensaje para notificar a los usuarios.');
      return;
    }
    const pendingEmails = emailInput.trim()
      ? [...emails, normalizeEmail(emailInput)].filter((v, i, a) => a.indexOf(v) === i)
      : emails;
    const originalSet = new Set(originalEmails.map((e) => normalizeEmail(e)));
    const pendingHasNew = pendingEmails.some((e) => !originalSet.has(normalizeEmail(e)));
    if (notifyUsers && !pendingHasNew) {
      setNotifyMessageError('Solo se notifica a personas nuevas. Agregá al menos un email nuevo.');
      return;
    }
    await doSubmit();
  };

  const handleRemoveInvitee = (email) => {
    const previous = emails;
    const previousEditors = editorEmails;
    setEmails((prev) => prev.filter((x) => x !== email));
    setEditorEmails((prev) => prev.filter((x) => x !== email));
    setEmailInputError('');
    toast.info('Se quitó al usuario de la lista.', {
      actionLabel: 'Deshacer',
      onAction: () => {
        setEmails(previous);
        setEditorEmails(previousEditors);
      },
      duration: 5000,
    });
  };

  const handleRevoke = async () => {
    if (!listId || guestBlocked) return;
    setSubmitting(true);
    setError('');
    try {
      if (isSpaceGuest) {
        const res = await unshareUserLotList({ list_id: listId, ownerUserId: spaceOwner });
        if (!res?._httpOk) {
          setError(
            resolveSpaceGuestWriteError(res?._httpStatus, spaceOwner)
            || readShareApiError(res, 'No se pudo desactivar el enlace. Intentalo de nuevo.'),
          );
          return;
        }
      } else {
        await unshareUserLotList({ list_id: listId });
      }
      if (shareUrl) setRevokedShareUrl(shareUrl);
      setShareToken('');
      setLinkStatus('inactive');
      setSavedAccessMode(accessMode === 'restricted' ? 'restricted' : 'public');
      setInactiveWarningVisible(true);
      setOriginalEmails([]);
      setEmails(mergeSharePeopleEmails([], editorEmails));
      setEmailInput('');
      setEmailInputError('');
      setNotifyUsers(false);
      setNotifyMessage('');
      setNotifyMessageError('');
      toast.success('El enlace fue desactivado.');
    } catch (e) {
      setError('No se pudo desactivar el enlace. Intentalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    const url = displayLinkUrl || shareUrl;
    if (!url) return;
    const ok = await copyToClipboard(url);
    if (ok) toast.success('Enlace copiado en el portapapeles.');
    else toast.error('No se pudo copiar automáticamente.');
  };

  const hasMembershipCounts = lotCount != null || unitCount != null;
  const displayLotCount = Number(lotCount) || 0;
  const displayUnitCount = Number(unitCount) || 0;
  const displayLotContentCount = Number(lotContentCount) || 0;
  const membershipSummary = hasMembershipCounts
    ? [
      `${displayLotCount} lote${displayLotCount !== 1 ? 's' : ''}`,
      `${displayUnitCount} unidad${displayUnitCount !== 1 ? 'es' : ''}`,
      displayLotContentCount > 0 ? `${displayLotContentCount} con contenido de lote` : null,
    ].filter(Boolean).join(' · ')
    : '';

  const shareLinkSection = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ color: TEXT_PRIMARY, fontSize: '16px', fontWeight: 500, mb: -1 }}>
          Enlace
        </Typography>
      </Box>

      <Typography sx={{ color: '#D4D4D4', fontSize: '12px', lineHeight: 1.4 }}>
        {effectiveLinkStatus === 'inactive'
          ? 'El enlace ya no está disponible. Generá uno nuevo para volver a compartir esta lista.'
          : accessMode === 'restricted'
            ? 'Solo personas con acceso pueden ver.'
            : effectiveLinkStatus === 'active'
              ? 'Copiá el enlace y compartilo con quien quieras.'
              : 'Todavía no existe un enlace para compartir esta lista.'}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 44,
          px: 1.25,
          borderRadius: '8px',
          bgcolor: '#242424',
          opacity: effectiveLinkStatus === 'inactive' ? 0.55 : 1,
        }}
      >
        {hasActiveLink ? (
          <LinkIcon sx={{ fontSize: 18, color: TEXT_SECONDARY, flexShrink: 0 }} />
        ) : (
          <LinkOffIcon sx={{ fontSize: 18, color: TEXT_MUTED, flexShrink: 0 }} />
        )}
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            color: hasActiveLink ? TEXT_PRIMARY : TEXT_MUTED,
            fontSize: '12px',
            fontFamily: typography.fontFamily.primary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayLinkUrl
            || 'Generá un enlace para que cualquier persona pueda acceder.'}
        </Typography>
        {displayLinkUrl ? (
          <Button
            size="small"
            onClick={handleCopyLink}
            disabled={effectiveLinkStatus === 'inactive'}
            startIcon={<ContentCopyIcon sx={{ fontSize: '16px !important' }} />}
            sx={{
              flexShrink: 0,
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 600,
              color: effectiveLinkStatus === 'inactive' ? TEXT_MUTED : colors.primaryLight,
              minWidth: 0,
              px: 1,
              '&:hover': {
                bgcolor: effectiveLinkStatus === 'inactive'
                  ? 'transparent'
                  : 'rgba(72,146,145,0.12)',
              },
              '&.Mui-disabled': {
                color: TEXT_MUTED,
                opacity: 1,
              },
              '&.Mui-disabled .MuiButton-startIcon': {
                color: TEXT_MUTED,
              },
            }}
          >
            Copiar
          </Button>
        ) : null}
      </Box>

      {effectiveLinkStatus === 'inactive' && inactiveWarningVisible && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            width: '100%',
            mb: 1,
            py: 1.5,
            pl: 2,
            pr: 1,
            bgcolor: '#242424',
            borderRadius: '8px',
            borderLeft: `3px solid ${WARNING_ACCENT}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: WARNING_ICON_BG,
              flexShrink: 0,
            }}
          >
            <PriorityHighIcon sx={{ fontSize: 14, color: '#fff' }} />
          </Box>
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontFamily: typography.fontFamily.primary,
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '18px',
              color: TEXT_PRIMARY,
            }}
          >
            El enlace anterior fue desactivado y ya no es accesible. Al generar uno nuevo, quienes tenían el enlace previo perderán el acceso.
          </Typography>
          <IconButton
            aria-label="Cerrar aviso"
            onClick={() => setInactiveWarningVisible(false)}
            size="small"
            sx={{
              color: 'rgba(255,255,255,0.85)',
              flexShrink: 0,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: SHARE_DIALOG_PAPER_SX }}
        {...brandsOverlap}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 2.5,
            py: 1.75,
            flexShrink: 0,
            color: TEXT_PRIMARY,
            fontFamily: typography.fontFamily.heading,
            fontSize: '18px',
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          Compartir
          <IconButton
            aria-label="Cerrar"
            onClick={handleClose}
            disabled={submitting}
            size="small"
            sx={{
              color: TEXT_SECONDARY,
              '&:hover': { color: TEXT_PRIMARY, bgcolor: 'rgba(255,255,255,0.06)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>

        <Divider sx={{ borderColor: BORDER }} />

        {listName ? (
          <Box sx={SHARE_DIALOG_LIST_META_SX}>
            <Tooltip title={`Lista: ${listName}`} placement="top" arrow>
              <Typography
                sx={{
                  color: TEXT_PRIMARY,
                  fontSize: '16px',
                  fontWeight: 500,
                  py: 0.25,
                  borderRadius: '4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  flex: '0 1 auto',
                  maxWidth: '55%',
                }}
              >
                {listName.toUpperCase()}
              </Typography>
            </Tooltip>
            {membershipSummary ? (
              <Typography
                sx={{
                  color: TEXT_MUTED,
                  fontSize: '12px',
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  ml: 'auto',
                }}
              >
                {membershipSummary}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        <DialogContent
          ref={contentRef}
          sx={{
            ...SHARE_DIALOG_CONTENT_SX,
            pt: listName ? 1.5 : 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {isInitializing ? (
              <ShareDialogContentSkeleton />
            ) : (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontWeight: 600 }}>
                    Acceso
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <AccessModeCard
                      selected={accessMode === 'public'}
                      disabled={submitting}
                      icon={<LanguageIcon sx={{ fontSize: 18 }} />}
                      title="Público"
                      description="Cualquier persona con el enlace puede ver, sin iniciar sesión."
                      onClick={() => {
                        setAccessMode('public');
                        // En público no hay observadores: Personas queda solo con editores.
                        setEmails([...editorEmails]);
                        setError('');
                      }}
                    />
                    <AccessModeCard
                      selected={accessMode === 'restricted'}
                      disabled={submitting}
                      icon={<LockOutlinedIcon sx={{ fontSize: 18 }} />}
                      title="Restringido"
                      description="Solo personas invitadas pueden acceder."
                      onClick={() => {
                        setAccessMode('restricted');
                        setEmails((prev) => mergeSharePeopleEmails(prev, editorEmails));
                        setError('');
                      }}
                    />
                  </Box>
                </Box>

                <FormControlLabel
                  sx={{ m: 0, alignItems: 'flex-start', gap: 1, ml: 0 }}
                  control={(
                    <Switch
                      size="small"
                      checked={showCadastralBase}
                      disabled={submitting}
                      onChange={(_event, checked) => setShowCadastralBase(checked)}
                      sx={{ ...FORM_SWITCH_SX, mt: 0.25 }}
                    />
                  )}
                  label={(
                    <Box>
                      <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontWeight: 600 }}>
                        Mostrar lotes catastrales de fondo
                      </Typography>
                      <Typography sx={{ color: TEXT_MUTED, fontSize: '12px', lineHeight: 1.4 }}>
                        Si está desactivado, el visitante solo ve los lotes de esta lista, sin el resto del catastro.
                      </Typography>
                    </Box>
                  )}
                />

                {/* Espacio ajeno + público: en público Personas es solo para
                    invitar editores, y eso es del dueño. */}
                {!(isSpaceGuest && accessMode === 'public') && (
                <Box sx={SHARE_SECTION_SHELL_SX}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ color: TEXT_PRIMARY, fontSize: '16px', fontWeight: 500, mb: -1 }}>
                      Personas
                    </Typography>
                    <Typography sx={{ color: TEXT_MUTED, fontSize: '12px', lineHeight: 1.4 }}>
                      {accessMode === 'public'
                        ? 'Invitá editores. En una lista pública cualquiera con el enlace ya puede ver.'
                        : (isSpaceGuest
                          ? 'Las personas que agregues pueden ver la lista. Invitar editores es del dueño del espacio.'
                          : 'Por defecto pueden ver la lista. Cambiá el permiso a Editor si querés que editen el contenido.')}
                    </Typography>
                    <Box
                      onClick={() => inputRef.current?.focus()}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        minHeight: 44,
                        px: 1.5,
                        py: 1,
                        borderRadius: '8px',
                        border: 'none',
                        bgcolor: '#242424',
                        cursor: 'text',
                        transition: 'box-shadow 0.15s',
                        '&:focus-within': {
                          boxShadow: emailInputError
                            ? '0 0 0 1px rgba(252,165,165,0.70)'
                            : `0 0 0 1px ${colors.primary}`,
                        },
                        '& input': SHARE_EMAIL_INPUT_SX,
                      }}
                    >
                      <MailOutlineIcon
                        sx={{ fontSize: 18, color: TEXT_SECONDARY, flexShrink: 0 }}
                      />
                      <input
                        ref={inputRef}
                        value={emailInput}
                        onChange={handleEmailChange}
                        onKeyDown={handleEmailKeyDown}
                        onBlur={handleEmailBlur}
                        placeholder="correo@ejemplo.com.ar"
                        autoComplete="email"
                      />
                    </Box>

                    {emailInputError && (
                      <Typography sx={{ color: DANGER, fontSize: '11px', ml: 0.5 }}>
                        {emailInputError}
                      </Typography>
                    )}

                    {emails.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                        <Box sx={INVITE_LIST_SX}>
                          {emails.map((email) => (
                            <Box
                              key={email}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.25,
                                minHeight: 40,
                                px: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: '50%',
                                  bgcolor: colors.primary,
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  fontFamily: typography.fontFamily.primary,
                                  letterSpacing: '0.02em',
                                }}
                                aria-hidden
                              >
                                {getEmailInitials(email)}
                              </Box>
                              <Typography
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  color: TEXT_PRIMARY,
                                  fontSize: '13px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {email}
                              </Typography>
                              {accessMode === 'public' || isSpaceGuest ? (
                                <Typography
                                  sx={{
                                    flexShrink: 0,
                                    color: TEXT_SECONDARY,
                                    fontSize: '12px',
                                    px: 1,
                                  }}
                                >
                                  {accessMode === 'public' ? 'Editor' : 'Puede ver'}
                                </Typography>
                              ) : (
                                <SharePersonRoleSelect
                                  email={email}
                                  disabled={submitting}
                                  value={editorEmails.includes(email) ? SHARE_PERSON_ROLE_EDITOR : SHARE_PERSON_ROLE_VIEWER}
                                  onChange={(role) => {
                                    setEditorEmails((prev) => {
                                      if (role === SHARE_PERSON_ROLE_EDITOR) {
                                        return prev.includes(email) ? prev : [...prev, email];
                                      }
                                      return prev.filter((item) => item !== email);
                                    });
                                  }}
                                />
                              )}
                              <Tooltip title={accessMode === 'public' ? 'Quitar editor' : 'Quitar acceso'} placement="top" arrow>
                                <IconButton
                                  aria-label={accessMode === 'public' ? `Quitar editor ${email}` : `Quitar acceso a ${email}`}
                                  size="small"
                                  disabled={submitting}
                                  onClick={() => handleRemoveInvitee(email)}
                                  sx={{
                                    color: TEXT_SECONDARY,
                                    flexShrink: 0,
                                    p: 0.5,
                                    '&:hover': { color: TEXT_PRIMARY, bgcolor: 'rgba(255,255,255,0.06)' },
                                  }}
                                >
                                  <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ))}
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            mt: 0.5,
                            pt: 1,
                            borderTop: `1px solid ${BORDER}`,
                          }}
                        >
                          <Checkbox
                            checked={notifyUsers}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setNotifyUsers(checked);
                              if (!checked) setNotifyMessageError('');
                            }}
                            disabled={submitting}
                            size="small"
                            sx={{
                              p: 0,
                              ml: 0,
                              color: TEXT_SECONDARY,
                              '&.Mui-checked': { color: colors.primary },
                            }}
                          />
                          <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px' }}>
                            Notificar a los usuarios
                          </Typography>
                        </Box>

                        {notifyUsers && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
                            <Typography sx={{ color: TEXT_PRIMARY, fontSize: '13px', fontWeight: 500 }}>
                              Mensaje
                            </Typography>
                            <Box
                              component="textarea"
                              value={notifyMessage}
                              onChange={(e) => {
                                setNotifyMessage(e.target.value.slice(0, 2000));
                                if (notifyMessageError) setNotifyMessageError('');
                              }}
                              placeholder="Escribí un mensaje para los nuevos invitados"
                              rows={3}
                              maxLength={2000}
                              disabled={submitting}
                              sx={{
                                width: '100%',
                                boxSizing: 'border-box',
                                resize: 'vertical',
                                minHeight: 72,
                                px: 1.5,
                                py: 1.25,
                                borderRadius: '8px',
                                border: `1px solid ${notifyMessageError ? DANGER : BORDER}`,
                                bgcolor: '#242424',
                                color: TEXT_PRIMARY,
                                fontFamily: typography.fontFamily.primary,
                                fontSize: '13px',
                                lineHeight: 1.4,
                                outline: 'none',
                                opacity: submitting ? 0.7 : 1,
                                '&::placeholder': { color: TEXT_MUTED },
                                '&:focus': {
                                  borderColor: notifyMessageError ? DANGER : colors.primary,
                                },
                              }}
                            />
                            {!!notifyMessageError && (
                              <Typography sx={{ color: DANGER, fontSize: '12px' }}>
                                {notifyMessageError}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                    </Box>
                  </Box>
                )}

                <Box sx={SHARE_SECTION_SHELL_SX}>
                  {shareLinkSection}
                </Box>

                <ShareTemplateSection
                  value={shareTemplate}
                  brandId={brandId}
                  onChange={(nextTemplate, nextBrandId) => {
                    setShareTemplate(nextTemplate);
                    setBrandId(nextBrandId);
                  }}
                  disabled={submitting}
                  locked={isSpaceGuest}
                />

                {!!error && (
                  <Typography sx={{ color: DANGER, fontSize: '12px' }}>
                    {error}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.75,
            gap: 1,
            justifyContent: 'flex-end',
            ...getShareDialogFooterScrollHintSx(canScrollMore),
          }}
        >
          {isInitializing ? (
            <Skeleton variant="rounded" width={140} height={40} sx={skFooterButtonSx} />
          ) : isPrivate ? (
            <>
              {hasActiveLink && (
                <Button
                  onClick={handleRevoke}
                  disabled={submitting || guestBlocked}
                  variant="outlined"
                  startIcon={<LinkOffIcon sx={{ fontSize: '16px !important' }} />}
                  sx={getShareDangerButtonSx({ busy: submitting })}
                >
                  Desactivar enlace
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || isInitializing || privatePrimaryDisabled || guestBlocked}
                sx={getSharePrimaryButtonSx({
                  busy: (submitting || isInitializing) && !privatePrimaryDisabled,
                })}
              >
                {privatePrimaryLabel}
              </Button>
            </>
          ) : hasActiveLink ? (
            <>
              <Button
                onClick={handleRevoke}
                disabled={submitting || guestBlocked}
                variant="outlined"
                startIcon={<LinkOffIcon sx={{ fontSize: '16px !important' }} />}
                sx={getShareDangerButtonSx({ busy: submitting })}
              >
                Desactivar enlace
              </Button>
              {publicDirty || (notifyUsers && hasNewInvitees) ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting || isInitializing || guestBlocked}
                  sx={getSharePrimaryButtonSx({ busy: submitting || isInitializing })}
                >
                  Actualizar
                </Button>
              ) : null}
            </>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || isInitializing || guestBlocked}
              startIcon={<LinkIcon sx={{ fontSize: '16px !important' }} />}
              sx={getSharePrimaryButtonSx({ busy: submitting || isInitializing })}
            >
              {effectiveLinkStatus === 'inactive' ? 'Generar nuevo enlace' : 'Generar enlace'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ShareUserLotListDialog;
